/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './SearchResultsMapV2.scss';

import * as React from 'react';

import { NoMapboxGlNotice, Icon, useBreakpoint } from 'sarsaparilla';

import {
    MapProvider,
    useMap,
    useMapMarkers,
    useMapPopup,
    useValidatedData,
    type MapProviderProps,
} from 'shared-ui/map';

import { useAppDispatch, useAppSelector } from '../../redux/redux-helpers';
import { showFullScreenMap } from '../../utils/mapUtils';
import { SearchResultItem } from '../../api/search.types';
import {
    fetchInventoryTypesAggregations,
    fetchSearchResults,
    updateSearchCriteria,
    updateSearchCriterias,
} from '../../actions/search';
import { setVisibleMapItems } from '../../actions/searchMapSync';

import {
    deriveSearchMapFraming,
    type SearchFramingResultsLocation,
} from './deriveSearchMapFraming';
import { fitBoundsToLocation } from './fitBoundsToLocation';
import { useVisibleMapItems } from './hooks/useVisibleMapItems';
import { SearchMapControls } from './SearchMapControls';
import { SearchResultCard } from '../SearchResultCard/SearchResultCard';
import { CardSurfaceContext } from '../SearchResultCard/CardSurfaceContext';
import { PaginatedPopupCarousel } from './mobile-popup-carousel/PaginatedPopupCarousel';
import { useMapControl } from '../../context/MapControlContext';

interface SearchResultsMapV2Props {
    data?: Array<SearchResultItem>;
    isFetching?: boolean;
    isMobile?: boolean;
    closeMobileExpanded?: () => void;
}

/** Search map default zoom, preserved from the legacy local Mapbox init. */
const SEARCH_MAP_DEFAULT_ZOOM = 5;

/** Fallback map center (Washington, D.C.) when no search center is derivable. */
const DEFAULT_MAP_CENTER: [number, number] = [-77.0369, 38.9072];

// Geographic center of the contiguous US, framed with a wide radius so the map
// zooms out to show the whole country. Used to convey "we searched everywhere
// and found nothing" when a text query matches no results and resolves to no
// location.
const US_OVERVIEW_CENTER = { lat: 39.8283, lng: -98.5795 };
const US_OVERVIEW_RADIUS_KM = 2500;

/**
 * Mapbox constructor options specific to the search map. Passed through the
 * shared `MapProvider`, which owns `container`/`style`/`center`/`zoom` and
 * defaults `attributionControl` to false.
 */
const SEARCH_MAP_OPTIONS: MapProviderProps['mapOptions'] = {
    dragRotate: false,
};

/**
 * Wrapper that owns the shared map provider. Computes the initial camera center
 * once (falling back to Washington, D.C.) and renders the canvas child, which
 * consumes the map instance via `useMap()`.
 */
export function SearchResultsMapV2(props: SearchResultsMapV2Props) {
    const searchState = useAppSelector((state) => state.search ?? {});
    const searchResultsState = useAppSelector(
        (state) => (state.searchResults ?? {}) as SearchFramingResultsLocation
    );

    // The provider reads the initial center once on mount. Derive it from the
    // same search state the canvas uses; fall back to Washington, D.C.
    const initialCenter = React.useMemo<[number, number]>(() => {
        const { searchCenter } = deriveSearchMapFraming(searchState, searchResultsState);
        if (searchCenter) {
            return [searchCenter.lng, searchCenter.lat];
        }
        return DEFAULT_MAP_CENTER;
    }, [searchState, searchResultsState]);

    return (
        <MapProvider
            initialCenter={initialCenter}
            zoom={SEARCH_MAP_DEFAULT_ZOOM}
            mapOptions={SEARCH_MAP_OPTIONS}
        >
            <SearchResultsMapCanvas {...props} />
        </MapProvider>
    );
}

/**
 * Canvas child: consumes the shared map context and owns the search-specific
 * marker, popup, visible-items, and controls logic (unchanged for now; fed the
 * map instance from context).
 */
function SearchResultsMapCanvas({
    data,
    isFetching,
    isMobile = false,
    closeMobileExpanded,
}: SearchResultsMapV2Props) {
    const dispatch = useAppDispatch();
    const { setFitBoundsToMarkers } = useMapControl();

    // Redux data
    const hoveredCard = useAppSelector((state) => {
        return (state.searchMapSync?.selectedCard || null) as SearchResultItem | null;
    });
    const searchState = useAppSelector((state) => state.search ?? {});
    // For generic queries the backend geocodes the text and returns the resolved
    // location here (not in `state.search`), so it's used as a fallback center below.
    const searchResultsState = useAppSelector(
        (state) => (state.searchResults ?? {}) as SearchFramingResultsLocation
    );

    // Local state
    const [isExpanded, setIsExpanded] = React.useState(false); // temporary - needs to be managed at the page level because it affects the layout
    const [activeItemId, setActiveItemId] = React.useState<string | null>(null);
    const skipNextFitBoundsRef = React.useRef(false);

    // Derived data
    // Derive the map's framing inputs (center, radius, and empty-state overview
    // flag) from the search criteria and geocoded response via a pure, isolated-
    // testable function. Recompute only when a framing-relevant field changes.
    const framing = React.useMemo(
        () => deriveSearchMapFraming(searchState, searchResultsState),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            searchState.lat,
            searchState.lng,
            searchState.map_center_lat,
            searchState.map_center_lng,
            searchState.latNe,
            searchState.latSw,
            searchState.lngNe,
            searchState.lngSw,
            searchState.radius,
            searchState.what,
            searchResultsState.lat,
            searchResultsState.lng,
            searchResultsState.radius,
        ]
    );

    const { searchRadiusKm, showUsOverviewWhenEmpty } = framing;

    // searchCenter stays current as searchState changes (unlike the provider's
    // initial center, which is read once at mount in the wrapper). It is used to
    // recenter the map when a search returns no markers to fit bounds to. Its
    // identity is kept stable across radius/query-text changes (it is a
    // downstream effect dependency).
    const searchCenter = React.useMemo(
        () => framing.searchCenter,
        // Depend on the coordinate values (not the object identity) so a new
        // framing object with the same center doesn't churn downstream effects.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [framing.searchCenter?.lat, framing.searchCenter?.lng]
    );

    const { isMdAndDown } = useBreakpoint();

    // Validate coordinates and fan out co-located markers via the shared helper.
    const validatedData = useValidatedData(data);

    const onMarkerClick = React.useCallback((id: string | null) => {
        setActiveItemId((prev) => (prev === id ? null : id));
    }, []);

    const activeItem = React.useMemo(() => {
        return validatedData.find((item) => item.id === activeItemId) ?? null;
    }, [validatedData, activeItemId]);

    // Map instance provided by the shared MapProvider.
    const { mapInstance, mapContainerRef, mapErrorType } = useMap();

    // Shared marker rendering: creates the `markers` source and pin/circle
    // layers, wires click/cursor interactions, and performs the initial
    // first-marker fit plus resize refits. The open popup (`activeCardId`) takes
    // precedence over card-list hover (`hoveredCardId`) for the `-active`
    // highlight, so both can be passed without extra coordination here.
    const { fitBoundsToMarkers } = useMapMarkers({
        data: validatedData,
        onMarkerClick,
        activeCardId: activeItemId,
        hoveredCardId: hoveredCard?.id ?? null,
    });

    // Publish fitBoundsToMarkers to context so it can be accessed by SearchResultsList
    React.useEffect(() => {
        setFitBoundsToMarkers(() => fitBoundsToMarkers);
        return () => {
            setFitBoundsToMarkers(undefined);
        };
    }, [fitBoundsToMarkers, setFitBoundsToMarkers]);

    // Search-specific fit orchestration, relocated from the (now removed) local
    // marker hook. The shared hook owns the initial first-marker fit; here we
    // re-fit when a *new* result set loads and frame the search location when a
    // result set has no markers.
    const lastFitDataRef = React.useRef<typeof validatedData | undefined>(undefined);
    const hasFitInitialMarkersRef = React.useRef(false);

    // True when the next result set should refit the map. Reset to false for one
    // result set after "Search Here" so the map keeps the user's framing. Keyed
    // on validatedData so the skip flag is consumed exactly once per result set.
    const shouldFitBounds = React.useMemo(() => {
        if (skipNextFitBoundsRef.current) {
            skipNextFitBoundsRef.current = false;
            return false;
        }
        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validatedData]);

    React.useEffect(() => {
        if (!mapInstance) return;

        const hasData = validatedData.length > 0;
        const dataChanged = validatedData !== lastFitDataRef.current;

        // Decide how to frame the map: fit to markers when present, otherwise
        // (once results have settled) frame the search location or the US.
        let doFit: (() => void) | null = null;
        let isMarkerFit = false;

        if (hasData) {
            // Only refit to markers when the result set itself changed. If this
            // effect re-runs because searchCenter/isFetching changed mid-search
            // (e.g. right after "Search Here"), the previous markers are still on
            // screen; refitting to them would snap back to the old location.
            if (!dataChanged) return;
            isMarkerFit = true;
            doFit = () => fitBoundsToMarkers();
        } else if (!isFetching && showUsOverviewWhenEmpty) {
            // A text query matched nothing and resolved to no location: zoom out
            // to the whole US rather than snapping back to the user's location.
            doFit = () =>
                fitBoundsToLocation(
                    mapInstance,
                    US_OVERVIEW_CENTER,
                    US_OVERVIEW_RADIUS_KM,
                    { isMobile: isMdAndDown }
                );
        } else if (!isFetching && searchCenter) {
            doFit = () =>
                fitBoundsToLocation(mapInstance, searchCenter, searchRadiusKm, {
                    isMobile: isMdAndDown,
                });
        }

        if (!doFit) return;

        // Remember the data we acted on so unrelated dep changes don't refit to it.
        lastFitDataRef.current = validatedData;

        // The shared hook performs the very first marker fit itself, so skip our
        // own fit for that initial result set to avoid a double animation.
        if (isMarkerFit && !hasFitInitialMarkersRef.current) {
            hasFitInitialMarkersRef.current = true;
            return;
        }

        if (shouldFitBounds) doFit();
    }, [
        validatedData,
        isFetching,
        searchCenter,
        searchRadiusKm,
        showUsOverviewWhenEmpty,
        shouldFitBounds,
        fitBoundsToMarkers,
        mapInstance,
        isMdAndDown,
    ]);

    // Shared popup: reads the map from context and manages a single Mapbox
    // popup tied to the active item (desktop-only; mobile uses the carousel).
    const { MapPopup, closePopup } = useMapPopup({ activeItem });

    // Track visible map items and sync to Redux
    const { visibleItemIds } = useVisibleMapItems({
        mapInstance,
        data: validatedData,
    });

    // Sync the IDs of items visible in the map viewport to Redux, so the results
    // list can narrow itself to those. null means "show all results" - dispatched
    // when every item (or no item) is in view, and on unmount to reset state.
    React.useEffect(() => {
        if (isMobile) {
            dispatch(setVisibleMapItems(null));
            return;
        }
        const noItemsInView = visibleItemIds?.length === 0;
        const allItemsInView = visibleItemIds?.length === validatedData.length;
        const shouldShowAll = noItemsInView || allItemsInView;

        dispatch(setVisibleMapItems(shouldShowAll ? null : visibleItemIds));

        return () => {
            dispatch(setVisibleMapItems(null));
        };
    }, [dispatch, isMobile, visibleItemIds, validatedData]);

    // Listen to redux filtersVisible changes and remove isExpanded if filters are opened
    React.useEffect(() => {
        if (searchState.filtersVisible) {
            setIsExpanded(false);
            dispatch(updateSearchCriteria('isMapExpanded', false));
            showFullScreenMap(true); // the logic here appears reversed, but it works
        }
    }, [dispatch, searchState.filtersVisible]);

    const onExpandClick = () => {
        dispatch(updateSearchCriteria('isMapExpanded', !isExpanded));

        if (!isExpanded) {
            dispatch(updateSearchCriteria('filtersVisible', false));
        }

        showFullScreenMap(isExpanded); // the logic here appears reversed, but it works

        if (mapInstance) {
            mapInstance.resize();
        }

        setIsExpanded((prev) => !prev);
        closeMobileExpanded?.();
    };

    const onSearchClick = () => {
        if (!mapInstance) return;

        const bounds = mapInstance.getBounds();

        if (bounds) {
            const rawNE = bounds.getNorthEast();
            const rawSW = bounds.getSouthWest();
            const lngSpan = rawNE.lng - rawSW.lng;

            const northEast = rawNE.wrap();
            const southWest = rawSW.wrap();
            const lat_ne = northEast.lat;
            const lat_sw = southWest.lat;

            // When the viewport spans >= 360° of longitude (the full world is visible),
            // wrapping each corner independently collapses the box into a tiny sliver.
            // Clamp to the full longitude range instead.
            const lng_ne = lngSpan >= 360 ? 180 : northEast.lng;
            const lng_sw = lngSpan >= 360 ? -180 : southWest.lng;

            const { lat, lng } = mapInstance.getCenter().wrap();

            setActiveItemId(null);

            // Set flag to skip fitting bounds for this search result
            skipNextFitBoundsRef.current = true;

            // clear previous criteria and search new bounds only
            dispatch(
                updateSearchCriterias({
                    cursor_mark: null,
                    lat: searchState.sort === 'distance' ? lat : null,
                    lng: searchState.sort === 'distance' ? lng : null,
                    radius: null,
                    what: null,
                    headerTextQuery: null,
                    location: null,
                    lat_ne,
                    lat_sw,
                    lng_ne,
                    lng_sw,
                    map_center_lat: lat,
                    map_center_lng: lng,
                    use_location: false,
                    placename: null,
                    start: null,
                    user_placename: null,
                })
            );

            window.setTimeout(() => {
                dispatch(fetchInventoryTypesAggregations());
                dispatch(fetchSearchResults());

                // Scroll to the top of the left rail after fetching new results
                // TODO: Move this logic to the list component because it should happen any time the data changes
                const cardsContainer = document.querySelector('.search-left-rail-wrap');

                if (cardsContainer) {
                    cardsContainer.scrollTo({
                        top: 0,
                    });
                }
            }, 0);
        }
    };

    if (mapErrorType) {
        return (
            <div className="search--search-results-map">
                <div id="map-container">
                    {mapErrorType === 'webgl' ? (
                        <NoMapboxGlNotice />
                    ) : (
                        <div className="search-map-error">
                            <Icon iconName="warning" size="lg" className="error-icon" />
                            <h2 className="heading-sm m-1">
                                Something went wrong while loading the map
                            </h2>
                            <p className="text-md">
                                Please refresh the page or try again in a moment.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="search--search-results-map">
            {/* Canvas first so keyboard tab order reaches the map before its
                controls; the control overlay is absolutely positioned over it. */}
            <div ref={mapContainerRef} id="map-container" />

            <SearchMapControls
                isExpanded={isExpanded || isMobile}
                onExpandClick={onExpandClick}
                onSearchClick={onSearchClick}
                isFetching={isFetching}
            />

            {activeItem ? (
                isMobile ? (
                    <PaginatedPopupCarousel
                        data={data}
                        activeItemId={activeItemId}
                        setActiveItemId={setActiveItemId}
                        fitBoundsToMarkers={fitBoundsToMarkers}
                    />
                ) : (
                    <MapPopup>
                        <CardSurfaceContext.Provider value="map-popup">
                            <SearchResultCard
                                item={activeItem}
                                isActive
                                hasCloseButton
                                shouldFocusOnMount
                                onCloseClick={() => {
                                    closePopup();
                                    setActiveItemId(null);
                                }}
                            />
                        </CardSurfaceContext.Provider>
                    </MapPopup>
                )
            ) : null}
        </div>
    );
}

// cSpell:ignore criterias, geoip
